import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Badge,
  Popconfirm,
  Tooltip,
  Spin,
  Statistic,
  Tabs,
  Progress
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  MessageOutlined,
  LockOutlined,
  FileTextOutlined,
  DashboardOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined
} from '@ant-design/icons';
import API from '../services/api'; // 导入统一的API实例
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);

  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('tickets');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    status: undefined,
    priority: undefined,
    search: '',
    hasSensitiveData: undefined
  });



  // 状态映射
  const statusMap = {
    open: { text: '待处理', color: 'orange' },
    in_progress: { text: '处理中', color: 'blue' },
    waiting_customer: { text: '等待客户', color: 'purple' },
    closed: { text: '已关闭', color: 'green' }
  };

  // 优先级映射
  const priorityMap = {
    low: { text: '低', color: 'green' },
    medium: { text: '中', color: 'orange' },
    high: { text: '高', color: 'red' },
    urgent: { text: '紧急', color: 'volcano' }
  };



  // 获取统计信息
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await API.get('/api/tickets/admin/statistics');
      if (response.data.code === 0) {
        setStatistics(response.data.data);
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取统计信息失败: ' + error.message);
    }
    setStatsLoading(false);
  }, []);

  // 获取工单列表
  const fetchTickets = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await API.get('/api/tickets', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
          ...params
        }
      });

      if (response.data.code === 0) {
        setTickets(response.data.data.tickets);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('获取工单列表失败: ' + error.message);
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, filters]);





  // 更新工单状态
  const handleUpdateTicketStatus = async (ticketId, status) => {
    try {
      const response = await API.put(`/api/tickets/${ticketId}`, { status });
      if (response.data.code === 0) {
        message.success('工单状态更新成功');
        fetchTickets();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('更新工单状态失败: ' + error.message);
    }
  };



  // 删除工单
  const handleDeleteTicket = async (ticketId) => {
    try {
      const response = await API.delete(`/api/tickets/${ticketId}`);
      if (response.data.code === 0) {
        message.success('工单删除成功');
        fetchTickets();
        fetchStatistics();
      } else {
        message.error(response.data.msg);
      }
    } catch (error) {
      message.error('删除工单失败: ' + error.message);
    }
  };



  // 表格列定义
  const columns = [
    {
      title: '工单编号',
      dataIndex: '_id',
      key: '_id',
      width: 150,
      render: (id) => <Text copyable>{id.slice(-8)}</Text>
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true
    },
    {
      title: '创建者',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 200,
      render: (user) => user?.username || '-'
    },

    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={statusMap[status]?.color}>{statusMap[status]?.text}</Tag>
      )
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority) => (
        <Tag color={priorityMap[priority]?.color}>{priorityMap[priority]?.text}</Tag>
      )
    },
    {
      title: '敏感数据',
      dataIndex: 'hasSensitiveData',
      key: 'hasSensitiveData',
      width: 100,
      render: (hasSensitiveData) => (
        hasSensitiveData
          ? <Tag color="red" icon={<LockOutlined />}>是</Tag>
          : <Tag color="green">否</Tag>
      )
    },
    {
      title: '回复数',
      dataIndex: 'replies',
      key: 'replies',
      width: 100,
      align: 'center',
      render: (replies) => {
        const count = replies?.length || 0;
        if (count === 0) {
          return (
            <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
              <MessageOutlined style={{ marginRight: '4px' }} />
              暂无回复
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Badge 
              count={count} 
              style={{ 
                backgroundColor: count > 5 ? '#f5222d' : count > 2 ? '#fa8c16' : '#52c41a',
                borderRadius: '10px',
                minWidth: '20px',
                height: '20px',
                lineHeight: '20px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            />
            <MessageOutlined style={{ marginLeft: '6px', color: '#1890ff', fontSize: '14px' }} />
          </div>
        );
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (date) => dayjs(date).format('MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="查看详情">
            <Button
              type="primary"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => navigate(`/admin/tickets/${record._id}`)}
            />
          </Tooltip>

          <Tooltip>
            <Select
              size="small"
              placeholder="状态"
              style={{ width: 100 }}
              value={record.status}
              onChange={(value) => handleUpdateTicketStatus(record._id, value)}
            >
              <Option value="open">待处理</Option>
              <Option value="in_progress">处理中</Option>
              <Option value="waiting_customer">等待客户</Option>
              <Option value="closed">已关闭</Option>
            </Select>
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确认删除此工单吗？"
              onConfirm={() => handleDeleteTicket(record._id)}
              okText="确认"
              cancelText="取消"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // 统计卡片
  const renderStatistics = () => {
    if (!statistics) return null;
    
    return (
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总工单数"
              value={statistics.overview.totalTickets}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待处理"
              value={statistics.overview.openTickets}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="处理中"
              value={statistics.overview.inProgressTickets}
              prefix={<SyncOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已关闭"
              value={statistics.overview.closedTickets}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="紧急工单"
              value={statistics.overview.urgentTickets}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="敏感数据工单"
              value={statistics.overview.ticketsWithSensitiveData}
              prefix={<LockOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="等待客户"
              value={statistics.overview.waitingCustomerTickets}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="近7天新增"
              value={statistics.overview.recentTickets}
              prefix={<PlusOutlined />}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  useEffect(() => {
    if (activeTab === 'tickets') {
      fetchTickets();
    } else if (activeTab === 'dashboard') {
      fetchStatistics();
    }
  }, [activeTab, fetchTickets, fetchStatistics]);



  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={3}>工单管理系统 - 管理员面板</Title>
        
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={<span><DashboardOutlined />统计概览</span>} key="dashboard">
            <Spin spinning={statsLoading}>
              {renderStatistics()}
            </Spin>
            
            {statistics && (
              <Row gutter={16}>
                <Col span={12}>
                  <Card title="优先级分布">
                    {statistics.priorityStats.map(item => {
                      const priorityInfo = priorityMap[item._id];
                      return (
                        <div key={item._id} style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>
                              <Tag color={priorityInfo?.color}>{priorityInfo?.text}</Tag>
                            </span>
                            <span>{item.count}</span>
                          </div>
                          <Progress 
                            percent={(item.count / statistics.overview.totalTickets * 100).toFixed(1)} 
                            strokeColor={priorityInfo?.color}
                            size="small"
                          />
                        </div>
                      );
                    })}
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title="工单状态统计">
                    <Row gutter={16}>
                      <Col span={12}>
                        <Progress
                          type="circle"
                          percent={((statistics.overview.closedTickets / statistics.overview.totalTickets) * 100).toFixed(1)}
                          format={() => `${statistics.overview.closedTickets}已完成`}
                        />
                      </Col>
                      <Col span={12}>
                        <Progress
                          type="circle"
                          percent={((statistics.overview.inProgressTickets / statistics.overview.totalTickets) * 100).toFixed(1)}
                          status="active"
                          format={() => `${statistics.overview.inProgressTickets}处理中`}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}
          </TabPane>
          
          <TabPane tab={<span><FileTextOutlined />所有工单</span>} key="tickets">
            {/* 筛选条件 */}
            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={4}>
                <Select
                  placeholder="选择状态"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.status}
                  onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                >
                  <Option value="open">待处理</Option>
                  <Option value="in_progress">处理中</Option>
                  <Option value="waiting_customer">等待客户</Option>
                  <Option value="closed">已关闭</Option>
                </Select>
              </Col>
              <Col span={4}>
                <Select
                  placeholder="选择优先级"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.priority}
                  onChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}
                >
                  <Option value="low">低</Option>
                  <Option value="medium">中</Option>
                  <Option value="high">高</Option>
                  <Option value="urgent">紧急</Option>
                </Select>
              </Col>

              <Col span={4}>
                <Select
                  placeholder="敏感数据"
                  allowClear
                  style={{ width: '100%' }}
                  value={filters.hasSensitiveData}
                  onChange={(value) => setFilters(prev => ({ ...prev, hasSensitiveData: value }))}
                >
                  <Option value="true">有敏感数据</Option>
                  <Option value="false">无敏感数据</Option>
                </Select>
              </Col>
              <Col span={8}>
                <Input.Search
                  placeholder="搜索工单标题或描述"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  onSearch={() => fetchTickets({ page: 1 })}
                  allowClear
                />
              </Col>
              <Col span={4}>
                <Button 
                  onClick={() => fetchTickets({ page: 1 })}
                  type="primary"
                  style={{ width: '100%' }}
                >
                  搜索
                </Button>
              </Col>
            </Row>

            {/* 工单表格 */}
            <Table
              columns={columns}
              dataSource={tickets}
              rowKey="_id"
              loading={loading}
              scroll={{ x: 1300 }}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, page, limit: pageSize }));
                }
              }}
            />
          </TabPane>
        </Tabs>
      </Card>




    </div>
  );
};

export default AdminTickets;